import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose, tap, always } from 'ramda';

import request, { APIOptions } from 'shared/api-request';
import { addHttpErrorNotification } from 'notifications/notifications.slice';
import { PayFreqSelectOption } from 'components/shared/PayFreqSelect';
import { selectSelectedCompany } from 'companies/companies.slice';
import store from 'store';

export function usePayFrequencies() {
  const dispatch = useDispatch<typeof store.dispatch>();
  const { compid } = useSelector(selectSelectedCompany) ?? { compid: undefined };

  const [ payFrequencies, setPayFrequencies ] = useState<PayFreqSelectOption[] | null>(null);

  const { current: abortCtrl } = useRef(new AbortController());
  const { current: setDefault } = useRef(compose(setPayFrequencies, always([])));
  const { current: handleError } = useRef(compose(
    tap(setDefault),
    dispatch,
    addHttpErrorNotification,
  ));
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    const uri = `/api/companies/${compid}/payfreqs`;
    const apiOpts: APIOptions = { method: 'GET', signal: abortCtrl.signal };
    request<PayFreqSelectOption[]>(uri, apiOpts)
      .then((result) => {
        if (mountedRef.current) {
          setPayFrequencies(result ?? []);
        }
      })
      .catch((err) => {
        if (mountedRef.current) {
          handleError(err);
        }
      });

    return () => {
      mountedRef.current = false;
      abortCtrl.abort();
    };
  }, [ handleError, abortCtrl, compid ]);

  return payFrequencies;
}
